import {
  QuestionGroupType,
  QuestionPType,
  TagCategory,
  type GetQuestionByIdQuery,
} from '@exam-management/graphql';
import type {
  QBankStructureLevel,
  QBankStructureLevelItemWithLevel,
} from '@exam-management/qbs-provider';
import { getCognitiveDomain } from '@exam-management/qcontent-management';

type QueryResult = GetQuestionByIdQuery['qctGetQuestionById'];

// eslint-disable-next-line etc/prefer-interface
type ParseContentForDisplayType = (content: string) => Promise<string>;

interface GetDetailSectionDefaultValueParams {
  details: QueryResult['details'] | undefined;
  choices: QueryResult['metadata']['choices'] | undefined;
  correctAnswer: QueryResult['correctAnswer'] | undefined;
  parseContentForDisplay: ParseContentForDisplayType;
}

async function getDetailSectionDefaultValues({
  details,
  choices,
  correctAnswer,
  parseContentForDisplay,
}: GetDetailSectionDefaultValueParams) {
  const parsedDetails = await parseContentForDisplay(details ?? '');
  const parsedCorrectAnswer = { value: correctAnswer?.value ?? undefined };

  const parsedChoices = [];
  for (const choice of choices ?? []) {
    const label = await parseContentForDisplay(choice.label);
    parsedChoices.push({
      order: choice.order,
      value: choice.value,
      label,
    });
  }

  return {
    details: parsedDetails,
    choices: parsedChoices.length
      ? parsedChoices
      : [
          { value: '1', label: '', order: 1 },
          { value: '2', label: '', order: 2 },
          { value: '3', label: '', order: 3 },
          { value: '4', label: '', order: 4 },
        ],
    correctAnswer: parsedCorrectAnswer,
  };
}

interface GetPropertiesSectionDefaultValueParams {
  solution: QueryResult['solution'] | undefined;
  reference: QueryResult['metadata']['reference'] | undefined;
  learningObjectiveStatement: QueryResult['learningObjectiveStatement'] | undefined;
  tags: QueryResult['tags'] | undefined;
  parseContentForDisplay: ParseContentForDisplayType;
}

async function getPropertiesSectionDefaultValues({
  solution,
  reference,
  learningObjectiveStatement,
  tags,
  parseContentForDisplay,
}: GetPropertiesSectionDefaultValueParams) {
  const parsedSolution = await parseContentForDisplay(solution?.value ?? '');
  const parsedReference = await parseContentForDisplay(reference ?? '');
  const cognitiveDomain = getCognitiveDomain(tags ?? []);

  const los = learningObjectiveStatement
    ? { label: learningObjectiveStatement.tag.name, value: learningObjectiveStatement.id }
    : null;

  return {
    solution: parsedSolution,
    reference: parsedReference,
    learningObjectiveStatement: los,
    cognitiveDomain,
  };
}

interface GetInformationSectionDefaultValueParams {
  tags: QueryResult['tags'] | undefined;
  pValue: QueryResult['pValue'] | undefined;
  pType: QueryResult['pType'] | undefined;
  pStats: QueryResult['metadata']['pStats'] | undefined;
  pInitial: QueryResult['pInitial'] | undefined;
  bIndexValue: QueryResult['bIndexValue'] | undefined;
}

function convertPTypeToSelectedValue(pType: QueryResult['pType'] | undefined) {
  switch (pType) {
    case QuestionPType.PStat:
      return 'pStat';
    case QuestionPType.PInitial:
    default:
      return 'pInitial';
  }
}

function getInformationSectionDefaultValues({
  tags,
  pType,
  pStats,
  pInitial,
  bIndexValue,
}: GetInformationSectionDefaultValueParams) {
  const calculation = (tags ?? []).find((tag) => tag.category === TagCategory.Calculation);

  const calculatedValue = calculation?.name === 'คำนวณ' ? 'true' : 'false';

  return {
    pValue: {
      selected: convertPTypeToSelectedValue(pType),
      pInitial: pInitial ?? undefined,
      pStat: pStats?.find((stat) => stat.selected)?.pValue,
    },
    calculated: calculation ? calculatedValue : '',
    bIndex: bIndexValue ?? '',
    pStats: pStats ?? null,
  };
}

type GetOwnersSectionDefaultValueParams = Pick<
  QueryResult['metadata'],
  'owner' | 'audit1' | 'audit2' | 'audit3'
>;

function getOwnersSectionDefaultValues({
  owner,
  audit1,
  audit2,
  audit3,
}: GetOwnersSectionDefaultValueParams) {
  return {
    owner: owner?.value ?? '',
    audit1: audit1 ?? '',
    audit2: audit2 ?? '',
    audit3: audit3 ?? '',
  };
}

interface GetLocationSectionDefaultValueParams {
  questionGroup: NonNullable<QueryResult['questionGroup']> | undefined;
  questionStructures: QueryResult['questionStructures'] | undefined;
  qbsLevels: QBankStructureLevel[];
  qbsLevelItems: QBankStructureLevelItemWithLevel[];
}

function getLocationSectionDefaultValues({
  questionGroup,
  questionStructures,
  qbsLevels,
  qbsLevelItems,
}: GetLocationSectionDefaultValueParams) {
  const { type, name } = questionGroup ?? {};

  const levelItemLocation: Record<string, { label: string; value: string } | undefined> = {};
  const locations = qbsLevelItems.filter((qBankStructureLevelItem) =>
    questionStructures?.some(
      (questionStructure) => questionStructure.refLevelItemId === qBankStructureLevelItem.id,
    ),
  );

  for (const level of qbsLevels) {
    const currentCategory = level.category;
    const levelItem = locations.find(
      (location) => location.qBankStructureLevel.category === currentCategory,
    );
    if (!levelItem) {
      levelItemLocation[currentCategory] = undefined;
      continue;
    }

    levelItemLocation[currentCategory] = {
      label: levelItem.tag?.name ?? 'ไม่ระบุ',
      value: levelItem.id,
    };
  }

  return {
    ...levelItemLocation,
    LOQ: {
      selected: type === QuestionGroupType.LotOfQuestion ? 'true' : 'false',
      value: type === QuestionGroupType.LotOfQuestion ? { label: name, value: name } : undefined,
    },
  };
}

export async function getQContentEditDefaultValues(
  questions: GetQuestionByIdQuery['qctGetQuestionById'] | undefined,
  qbsLevels: QBankStructureLevel[],
  qbsLevelItems: QBankStructureLevelItemWithLevel[],
  parseContentForDisplay: ParseContentForDisplayType,
) {
  if (!questions?.questionGroup) return;

  const {
    pValue,
    pType,
    pInitial,
    bIndexValue,
    tags,
    metadata,
    questionStructures,
    questionGroup,
    details,
    correctAnswer,
    solution,
    learningObjectiveStatement,
  } = questions;
  const { choices, reference, audit1, audit2, audit3, owner, pStats } = metadata;

  const location = getLocationSectionDefaultValues({
    questionGroup,
    questionStructures,
    qbsLevels,
    qbsLevelItems,
  });

  const detail = await getDetailSectionDefaultValues({
    details,
    choices,
    correctAnswer,
    parseContentForDisplay,
  });

  const properties = await getPropertiesSectionDefaultValues({
    solution,
    reference,
    learningObjectiveStatement,
    tags,
    parseContentForDisplay,
  });

  const information = getInformationSectionDefaultValues({
    tags,
    pValue,
    pType,
    pInitial,
    pStats,
    bIndexValue,
  });

  const owners = getOwnersSectionDefaultValues({ owner, audit1, audit2, audit3 });

  return {
    detail,
    properties,
    information,
    owners,
    location,
  };
}
